import { FC } from "react";
import { Box, styled } from "@mui/system";
import muiIcons from "../../../components/icons/mui-icons";
import Containers from "../../../components/Containers";
import { CardContainer } from "../../../components/CardContainer";
import PolarChart from "../../../components/apexCharts/PolarChart";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import BasicAreaChart from "../../../components/apexCharts/BasicAreaChart";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { tableData, activityTimeLineData } from "./dashboard4-data";
import {
  Avatar,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import {
  H2,
  H3,
  H4,
  H5,
  Paragraph,
  Span,
} from "../../../components/Typography";

const StyledAvatar = styled(Avatar)(() => ({
  width: 80,
  height: 80,
  margin: "auto",
  marginTop: "10px",
  marginBottom: "10px",
}));

const UserCardFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: "15px",
  borderTop: `1px solid ${theme.palette.grey[300]}`,
  "& h5": {
    display: "flex",
    alignItems: "center",
    "& svg": {
      fontSize: 22,
      marginRight: "5px",
    },
  },
}));

const ChartHeader = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const StyledTable = styled(Table)(({ theme }) => ({
  "& thead": {
    "& tr": {
      "& th": {
        padding: ".2rem .75rem",
        fontWeight: "bold",
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        background: theme.palette.mode === "light" && "#F0F3FF",
      },
    },
  },
  "& tbody": {
    "& tr": {
      "& td": {
        border: "none",
        padding: ".8rem .6rem",
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
      },
      "&:last-child td": {
        borderBottom: "none",
      },
    },
  },
}));

const Status = styled(Span)<{ status: string }>(({ theme, status }) => ({
  fontSize: 12,
  borderRadius: "30px",
  padding: "4px 12px",
  color:
    status === "Cancel"
      ? theme.palette.error.main
      : status === "Pending"
      ? theme.palette.warning.main
      : status === "Success" && theme.palette.success.main,
  border: `1px solid ${
    status === "Cancel"
      ? theme.palette.error.main
      : status === "Pending"
      ? theme.palette.warning.main
      : status === "Success" && theme.palette.success.main
  }`,
}));

const ProgressBar = styled(LinearProgress)<{ progress: number }>(
  ({ theme, progress }) => ({
    height: 20,
    borderRadius: 10,
    [`& .${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[400],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 10,
      backgroundColor:
        progress < 40
          ? theme.palette.error.main
          : progress < 60
          ? theme.palette.warning.main
          : theme.palette.success.main,
    },
  })
);

const Dashboard4: FC = () => {
  const { ChartContainer, TableContainer } = Containers();
  const { FacebookIcon, TwitterIcon, InstagramIcon, PersonIcon, CartIcon } =
    muiIcons;
  const theme = useTheme();
  const sideCardData = [
    {
      name: "Backups",
      subName: "Total: 12",
      Icon: muiIcons.ServerIcon,
      color: theme.palette.primary.main,
    },
    {
      name: "Databases",
      subName: "Total: 12",
      Icon: muiIcons.DatabaseIcon,
      color: theme.palette.secondary.main,
    },
    {
      name: "Space Used",
      subName: "Total: 160GB",
      Icon: muiIcons.InboxIcon,
      color: theme.palette.success.main,
    },
    {
      name: "Downloaded",
      subName: "Total: 30GB",
      Icon: muiIcons.DownloadIcon,
      color: theme.palette.warning.main,
    },
  ];

  return (
    <>
      <Breadcrumb name="Dashboard" subName="Version 4" path="/dashboard/v1" />
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <CardContainer
            sx={{
              padding: 0,
              backgroundImage: `url("/assets/images/congrats-bg.png")`,
              backgroundSize: "100%",
              backgroundRepeat: "no-repeat",
            }}
          >
            <Box textAlign="center" padding="2rem 1rem">
              <img src="/assets/images/gold_badge.svg" width="100px" alt="" />
              <Box my="10px">
                <H2 color="warning.main">Congratulations!</H2>
                <H3>Jennifer Lawrence</H3>
                <Paragraph>
                  You earn bonus point as affiliate marketer, Great Job.
                </Paragraph>
              </Box>
              <Box mt="20px">
                <H2 color="primary.main">$48.3k</H2>
                <GullButton rounded={true} variant="contained" color="primary">
                  View Sales
                </GullButton>
              </Box>
            </Box>
          </CardContainer>
        </Grid>

        <Grid item xs={12} md={4}>
          <CardContainer sx={{ textAlign: "center", minHeight: 365 }}>
            <StyledAvatar src="/assets/images/faces/4.jpg" />
            <H3>Jenifer Lawrence</H3>
            <Paragraph sx={{ fontSize: 12 }}>Digital Marketer</Paragraph>
            <Paragraph sx={{ my: "6px" }}>
              Create usable interface and designs@uilib.com. Lorem ipsum is a
              placeholder text commonly used to demonstrate the visual form of a
              document.
            </Paragraph>
            <Box my="10px">
              <IconButton>
                <FacebookIcon />
              </IconButton>
              <IconButton>
                <TwitterIcon />
              </IconButton>
              <IconButton>
                <InstagramIcon />
              </IconButton>
            </Box>
            <UserCardFooter>
              <H5>
                <PersonIcon sx={{ color: "warning.main" }} /> 5.0
              </H5>
              <H5>
                <CartIcon sx={{ color: "primary.main" }} /> Sale 30
              </H5>
            </UserCardFooter>
          </CardContainer>
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={3}>
            {sideCardData.map((data) => {
              const { name, subName, Icon, color } = data;

              return (
                <Grid item xs={12} md={3}>
                  <CardContainer
                    sx={{
                      display: "flex",
                      padding: "40px 10px",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon sx={{ color: color, fontSize: 45, mr: "10px" }} />
                    <Box>
                      <H4>{name}</H4>
                      <Paragraph>{subName}</Paragraph>
                    </Box>
                  </CardContainer>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        <Grid item xs={12} md={8}>
          <ChartContainer sx={{ p: 0, height: 380 }}>
            <Box padding="1rem">
              <ChartHeader
                mb="10px"
                pb="10px"
                borderBottom={`1px solid ${theme.palette.grey[300]}`}
              >
                <H5>Finance Summary</H5>
                <GullButton
                  sx={{ padding: ".1rem 1rem" }}
                  rounded={true}
                  variant="outlined"
                  color="primary"
                >
                  Outlined
                </GullButton>
              </ChartHeader>

              <ChartHeader>
                <Box textAlign="left">
                  <Paragraph>Final Commission Revenue</Paragraph>
                  <H3>$790,505</H3>
                </Box>
                <Box textAlign="right">
                  <Paragraph>Final Commission Revenue</Paragraph>
                  <H3>July 26,2021</H3>
                </Box>
              </ChartHeader>
            </Box>
            <BasicAreaChart
              height={260}
              color={theme.palette.primary.main}
              data={[40, 45, 50, 45, 50, 60, 70, 75, 80, 85, 90, 95]}
            />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={4}>
          <ChartContainer sx={{ height: 380 }}>
            <H5>Affiliated Sales</H5>
            <PolarChart />
          </ChartContainer>
        </Grid>

        <Grid item xs={12} md={8}>
          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell align="left">Thumbnail</TableCell>
                  <TableCell align="left">Title</TableCell>
                  <TableCell align="left">Status</TableCell>
                  <TableCell align="left">Created On</TableCell>
                  <TableCell align="left">Progress</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((data, index) => (
                  <TableRow key={index}>
                    <TableCell
                      align="left"
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <img src={data.photoUrl} width="50px" alt="" />
                    </TableCell>
                    <TableCell align="left">{data.title}</TableCell>
                    <TableCell align="left">
                      <Status status={data.status}>{data.status}</Status>
                    </TableCell>
                    <TableCell align="left">{data.created}</TableCell>
                    <TableCell align="left">
                      <Box position="relative" width="100%">
                        <ProgressBar
                          variant="determinate"
                          value={data.progress}
                          progress={data.progress}
                        />
                        <Span
                          sx={{
                            fontSize: "12px",
                            position: "absolute",
                            top: "1.5px",
                            left: "10px",
                            color: "white",
                          }}
                        >
                          {data.progress}%
                        </Span>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </Grid>
        <Grid item xs={12} md={4}>
          <CardContainer>
            <H3>Activity Timeline</H3>
            {activityTimeLineData.map((data) => {
              const { name, Icon, created, details } = data;
              return (
                <Box my="13.5px" display="flex" alignItems="center">
                  <IconButton
                    sx={{
                      mr: "10px",
                      background: theme.palette.primary.main,
                      padding: "4px",
                      "&:hover": {
                        background: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        color: "white",
                        fontSize: "22px",
                      }}
                    />
                  </IconButton>
                  <Box>
                    <H5>{name}</H5>
                    <Paragraph fontSize="12px">{details}</Paragraph>
                    <Span fontSize="12px">{created}</Span>
                  </Box>
                </Box>
              );
            })}
          </CardContainer>
        </Grid>
      </Grid>
    </>
  );
};

export default Dashboard4;
